import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, CloseButton, Dialog, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { Card, PageContainer, ResponsiveModal, ResponsiveTable, type ResponsiveColumn } from '../../components/shared'
import { UnionOnly } from '../../components/scoping/UnionOnly'
import { useCampaignDetail, useCampaignSignals, useResolveSignal, SIGNALS_PAGE_SIZE } from '../../hooks/useCampaign'
import { extractApiMessage } from '../../lib/apiError'
import { SIGNAL_HEADINGS, type SignalCategory, type SignalItem, type SignalResolution } from '../../types/unionSignals'

// O2 Union Signals: organizer queue of what canvassers reported at the door. Treatment B (table).
// Four independently paged groups; each row resolves via POST .../signals/{sessionId}/resolve.

type Resolve = (item: SignalItem, action: SignalResolution) => Promise<void>

interface GroupSpec {
  category: SignalCategory
  heading: string
  lede: string
}

const GROUPS: GroupSpec[] = [
  { category: 'DoesntWorkHere', heading: SIGNAL_HEADINGS.DoesntWorkHere, lede: "Canvassers didn't find these people at their listed worksite." },
  { category: 'DoesntLiveHere', heading: SIGNAL_HEADINGS.DoesntLiveHere, lede: "Canvassers were told these people don't live at the address." },
  { category: 'MissingMember', heading: SIGNAL_HEADINGS.MissingMember, lede: "Members met at the door who weren't in the system." },
  { category: 'RecruitmentLead', heading: SIGNAL_HEADINGS.RecruitmentLead, lede: 'Non-members who talked with a canvasser.' },
]

const ANSWER_LABEL: Record<string, string> = { Yes: 'Member', No: 'Not a member', NotSure: 'Not sure' }

const touch = { minH: '44px', _touch: { minH: '44px' } } as const

type Confirm = { kind: 'end' | 'apply'; item: SignalItem }

function formatDate(iso: string) {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

function SignalGroup({
  spec, campaignId, resolve, busy,
}: { spec: GroupSpec; campaignId: string; resolve: Resolve; busy: boolean }) {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const { data, isLoading, error } = useCampaignSignals(campaignId, spec.category, page)
  const [confirm, setConfirm] = useState<Confirm | null>(null)

  const offList = spec.category === 'MissingMember' || spec.category === 'RecruitmentLead'
  const isWork = spec.category === 'DoesntWorkHere'
  const items = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const pageSize = data?.pageSize ?? SIGNALS_PAGE_SIZE
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))

  // Resolving the last row on a trailing page shrinks the group: pull back to the last real page
  // so the user is never stranded on an empty page with no Previous button.
  useEffect(() => {
    if (data && page > pageCount) setPage(pageCount)
  }, [data, page, pageCount])

  const run = async (item: SignalItem, action: SignalResolution) => {
    setConfirm(null)
    await resolve(item, action)
  }

  const actionsFor = (row: SignalItem) => {
    const btn = (label: string, onClick: () => void) => (
      <Button key={label} size="sm" variant="outline" {...touch} disabled={busy}
        onClick={(e) => { e.stopPropagation(); onClick() }}>
        {label}
      </Button>
    )
    const out: React.ReactNode[] = []
    if (isWork && row.canEndEmployment) out.push(btn('End employment', () => setConfirm({ kind: 'end', item: row })))
    if (spec.category === 'DoesntLiveHere') out.push(btn('Confirm moved', () => run(row, 'ConfirmedMoved')))
    if (offList && (row.offListPhone || row.offListEmail)) out.push(btn('Apply phone/email', () => setConfirm({ kind: 'apply', item: row })))
    out.push(btn('Dismiss', () => run(row, 'Dismissed')))
    return <HStack gap={2} flexWrap="wrap" justify={{ base: 'flex-start', md: 'flex-end' }}>{out}</HStack>
  }

  const columns: ResponsiveColumn<SignalItem>[] = [
    { header: 'Name', mobileRole: 'title', cell: (r) => <Text>{r.contactName}</Text> },
    isWork
      ? { header: 'Worksite', mobileRole: 'meta', cell: (r) => <Text>{r.worksiteLabel ?? ''}</Text> }
      : { header: 'Address', mobileRole: 'meta', cell: (r) => <Text>{r.contactAddress ?? ''}</Text> },
    { header: 'Visited', mobileRole: 'meta', cell: (r) => <Text>{formatDate(r.sessionDate)}</Text> },
    ...(offList
      ? [
          { header: 'Member answer', mobileRole: 'meta' as const, cell: (r: SignalItem) => <Text>{r.memberAnswer ? ANSWER_LABEL[r.memberAnswer] : ''}</Text> },
          {
            header: 'Phone / email', mobileRole: 'meta' as const,
            cell: (r: SignalItem) => <Text>{[r.offListPhone, r.offListEmail].filter(Boolean).join(' · ')}</Text>,
          },
        ]
      : []),
    {
      header: offList ? 'Context' : 'Notes', mobileRole: 'desktop-only',
      cell: (r) => <Text color="muted">{(offList ? r.offListContext : r.notes) ?? r.notes ?? ''}</Text>,
    },
    { header: 'Actions', mobileRole: 'badge', cell: actionsFor },
  ]

  const target = confirm?.item
  return (
    <Card>
      <VStack align="stretch" gap={3}>
        <VStack align="start" gap={0}>
          <Heading as="h3" size="md">{spec.heading}</Heading>
          <Text fontSize="sm" color="muted">{spec.lede}</Text>
        </VStack>

        {isLoading && <VStack py={6}><Spinner size="lg" color="accent" /></VStack>}
        {!!error && <Text color="danger.fg">{extractApiMessage(error, "Couldn't load this group.")}</Text>}
        {!isLoading && !error && (
          <ResponsiveTable
            bare
            columns={columns}
            rows={items}
            keyFn={(r) => r.sessionId}
            onRowClick={(r) => navigate(`/dashboard/contacts/${r.contactId}`)}
            emptyMessage="Nothing to review."
          />
        )}

        {(totalCount > pageSize || page > 1) && (
          <HStack justify="space-between">
            <Button size="sm" variant="outline" {...touch} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Text fontSize="sm" color="muted">Page {page} of {pageCount}</Text>
            <Button size="sm" variant="outline" {...touch} disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </HStack>
        )}
      </VStack>

      <ResponsiveModal isOpen={!!confirm} onClose={() => setConfirm(null)} size="sm">
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>{confirm?.kind === 'end' ? 'End employment?' : 'Apply phone/email?'}</Dialog.Header>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} />
            </Dialog.CloseTrigger>
            <Dialog.Body>
              {target && confirm?.kind === 'end' && (
                <Text>
                  {`End ${target.contactName}'s job at ${target.worksiteLabel ?? 'this worksite'}? The job stays on their record as ended.`}
                </Text>
              )}
              {target && confirm?.kind === 'apply' && (
                <VStack align="stretch" gap={2}>
                  <Text>{`Update ${target.contactName}'s contact info?`}</Text>
                  {target.offListPhone && (
                    <Text fontSize="sm">{`Phone: ${target.contactPhone ?? '(none)'} → ${target.offListPhone}`}</Text>
                  )}
                  {target.offListEmail && (
                    <Text fontSize="sm">{`Email: ${target.contactEmail ?? '(none)'} → ${target.offListEmail}`}</Text>
                  )}
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <HStack gap={3}>
                <Button variant="outline" size="sm" {...touch} onClick={() => setConfirm(null)}>Cancel</Button>
                <Button
                  colorPalette="brand" size="sm" {...touch} disabled={busy}
                  onClick={() => target && run(target, confirm?.kind === 'end' ? 'EmploymentEnded' : 'ContactInfoApplied')}
                >
                  {confirm?.kind === 'end' ? 'End employment' : 'Apply phone/email'}
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </Card>
  )
}

export function CampaignSignalsPage() {
  const { id } = useParams<{ id: string }>()
  const { data: campaign } = useCampaignDetail(id)
  const resolveMutation = useResolveSignal(id)
  const { mutateAsync, isPending } = resolveMutation

  const resolve: Resolve = async (item, action) => {
    try {
      await mutateAsync({ sessionId: item.sessionId, action })
      toaster.create({ type: 'success', title: 'Signal resolved' })
    } catch (err) {
      toaster.create({ type: 'error', title: extractApiMessage(err, "Couldn't resolve this signal") })
    }
  }

  return (
    <PageContainer
      title="Union signals"
      subtitle={campaign?.label}
      back={id ? { to: `/dashboard/campaigns/${id}`, label: 'Back to campaign' } : null}
    >
      <UnionOnly fallback={<Text>Not available</Text>}>
        <VStack gap={6} align="stretch">
          {id && GROUPS.map((g) => (
            <SignalGroup key={g.category} spec={g} campaignId={id} resolve={resolve} busy={isPending} />
          ))}
        </VStack>
      </UnionOnly>
    </PageContainer>
  )
}
